import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {useMemo, useState} from 'react';
import {createSelector} from 'reselect';

import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isTeamNamespace} from '../../../../../helpers/models/namespaceHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/reduxHelpers';
import {AppParametricSelector} from '../../../../../helpers/reselectHelpers';
import {DismissedAlertsEnum} from '../../../../../models/api/preferencesModel';
import {getConversations} from '../../../../../store/data/dataSelectors';
import {getInboxes} from '../../../../../store/data/inboxes/inboxesSelectors';
import {updateDismissedAlertsAction} from '../../../../../store/data/preferences/preferencesActions';
import {getDismissedAlerts} from '../../../../../store/data/preferences/preferencesSelectors';
import {getHasAutoShareDrafts} from '../../../../../store/featureFlags/featureFlagsSelectors';
import {OkCancelDialog} from '../../../../common/dialogs/okCancelDialog';
import {InboxesFormatter} from '../../../../common/formatters/inboxesFormatter';

function makeGetDraftsAutoShareProps(getConversationId: AppParametricSelector<any, number | undefined>) {
  return createSelector(
    [getConversationId, getConversations, getDismissedAlerts, getInboxes, getHasAutoShareDrafts],
    (conversationId, conversations, dismissedAlerts, inboxes, hasAutoShareDrafts) => {
      if (dismissedAlerts.includes(DismissedAlertsEnum.AUTO_SHARE_DRAFTS)) {
        return {shouldShowAlert: false};
      }

      if (!hasAutoShareDrafts) {
        return {shouldShowAlert: false};
      }

      if (!conversationId) {
        return {shouldShowAlert: false};
      }

      const conversation = conversations[conversationId];
      if (!conversation) {
        return {shouldShowAlert: false};
      }

      const teamInboxes = _(conversation.inboxIds)
        .map((i) => inboxes[i])
        .compact()
        .filter((i) => isTeamNamespace(i.namespace))
        .value();

      if (teamInboxes.length < 1) {
        return {shouldShowAlert: false};
      }

      return {shouldShowAlert: true, inboxes: teamInboxes};
    },
  );
}

export function useAutoShareDraftAlert(conversationId: number | undefined) {
  const getDraftsAutoShareProps = useMemo(
    () => makeGetDraftsAutoShareProps(() => conversationId),
    [conversationId],
  );
  const draftAutoShareProps = useAppSelector(getDraftsAutoShareProps);
  const [onAutoShareDialogConfirm, setOnAutoShareDialogConfirm] = useState<{handler: Handler | null}>({
    handler: null,
  });
  const dispatch = useAppDispatch();

  const maybeRenderDialog = () => {
    if (
      !onAutoShareDialogConfirm.handler ||
      !draftAutoShareProps.inboxes ||
      !draftAutoShareProps.shouldShowAlert
    ) {
      return null;
    }

    return (
      <OkCancelDialog
        title={<Trans id="autoshareddrafts-title">Auto shared drafts</Trans>}
        onRequestClose={() => {
          dispatch(updateDismissedAlertsAction(DismissedAlertsEnum.AUTO_SHARE_DRAFTS));
          setOnAutoShareDialogConfirm({handler: null});
        }}
        onOkClick={() => {
          dispatch(updateDismissedAlertsAction(DismissedAlertsEnum.AUTO_SHARE_DRAFTS));
          onAutoShareDialogConfirm.handler?.();
          setOnAutoShareDialogConfirm({handler: null});
        }}
      >
        <Trans id="autoshareddrafts-copy">
          This draft will be visible and editable by all teammates in{' '}
          <InboxesFormatter
            inboxes={draftAutoShareProps.inboxes}
            limit={2}
            renderInboxName={(name) => <strong>{name}</strong>}
          />
          . Notify a teammate by @mentioning them in a comment below.
        </Trans>
      </OkCancelDialog>
    );
  };

  const maybeRegisterHandler = (handler: Handler) => {
    if (!draftAutoShareProps.shouldShowAlert) {
      handler();
    } else {
      setOnAutoShareDialogConfirm({handler});
    }
  };

  return {maybeRenderDialog, maybeRegisterHandler};
}
